import React from 'react';
import { Element } from 'react-scroll';
import * as Yup from 'yup';
import {
  SubForm,
  FormSection,
  Heading,
  Box,
  FormTextInput,
  FormRadioGroup,
  RadioButton,
  FlexGrid,
  FlexGridItem,
  FormMaskedTextInput,
  FormCheckBox,
} from '@alinta-digital/asg';
import * as icon from '@alinta-digital/asg/icons/';
import { FormContainerContext } from '../../contexts/FormContainerContext';
import {
  redirectToCreditCardPaymentPortal,
  validateBsbNumber,
} from '../../components/DebitDetails/';
import { ErrorNotification } from '../../components/ErrorNotification';
import {
  setFormContextBranchDetails,
  setFormContextError,
  scrollToScrollElement,
  resetFormContextError,
} from '../../utils/utils';
import { DirectDebitForm } from '../../views/Form/Form';
import { PaymentCardType } from '@alinta-digital/payment-form/types';
import { ButtonV2, StyledLink, StyledText } from '../../Common.Styled';
import { Field, FormikProps } from 'formik';

interface DebitDetailsSubForm {
  paymentMethod: string;
  bankAccountName: string;
  bsbNumber: string;
  bankAccountNumber: string;
  accountConfirmation: boolean;
  collectDataConfirmation: boolean;
  invoiceConfirmation: boolean;
  cardType?: PaymentCardType;
  cardName?: string;
  creditCard?: {
    code: number;
    expiryMonth: string;
    expiryYear: string;
    last4: string;
    token: string;
  };
}

export interface DebitDetailsParentForm {
  debitDetails: DebitDetailsSubForm;
}

interface DebitDetailsProps {
  formikProps: FormikProps<DirectDebitForm>;
  isEdit: boolean;
  onPreviousClick: () => void;
  onNextClick: () => void;
}

export const DebitDetailsSubForm: React.FC<DebitDetailsProps> = ({
  formikProps,
  onPreviousClick,
  onNextClick,
}) => {
  const LoaderIcon = icon.Loader;
  const {
    debitDetails: debitDetailParentValues,
    identifyYourAccountForm: identifyYourAccountParentValues,
  } = formikProps.values;
  const {
    electricity: parentFormElectricity,
    gas: parentFormGas,
  } = formikProps.values.connection;

  const {
    values: formContextValues,
    dispatch: formContextDispatch,
  } = React.useContext(FormContainerContext);
  const {
    error: formContextError,
    bankBranch: formContextBankBranch,
  } = formContextValues;

  const [disableNext, setDisableNext] = React.useState(false);
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const [loadingBsbField, setLoadingBsbField] = React.useState(false);
  const [typingBsbField, setTypingBsbField] = React.useState(false);
  const [bankBranch, setBankBranch] = React.useState({
    valid: formContextBankBranch.valid,
    bsbNumber: formContextBankBranch.bsbNumber,
    displayName: formContextBankBranch.displayName,
    hideDisplayName: formContextBankBranch.hideDisplayName,
  });

  React.useEffect(() => {
    if (formContextError.steps[2].status) {
      scrollToScrollElement('notificationError');
    } else {
      scrollToScrollElement('debitDetailsStart');
    }
  }, [formContextError.steps]);

  React.useEffect(() => {
    if (
      document.getElementById('debitDetails.bankAccountNumber') &&
      bankBranch.valid &&
      !loadingBsbField &&
      !typingBsbField
    )
      document.getElementById('debitDetails.bankAccountNumber')?.focus();
  }, [bankBranch.valid, loadingBsbField, typingBsbField]);

  return (
    <SubForm<DebitDetailsParentForm, DebitDetailsSubForm>
      formikProps={formikProps as any}
      valuesKey="debitDetails"
      config={{
        debitDetails: {
          paymentMethod: {
            validation: Yup.string().required('Please select a payment method'),
            initialValue: debitDetailParentValues.paymentMethod,
          },
          accountConfirmation: {
            validation: Yup.boolean().test(
              'validation',
              'Need to read and agree',
              (value) => value
            ),
            initialValue: debitDetailParentValues.accountConfirmation,
          },
          collectDataConfirmation: {
            validation: Yup.boolean().test(
              'validation',
              'Need to read and agree',
              (value) => value
            ),
            initialValue: debitDetailParentValues.collectDataConfirmation,
          },
          invoiceConfirmation: {
            validation: Yup.boolean().test(
              'validation',
              'Need to read and agree',
              (value) => value
            ),
            initialValue: debitDetailParentValues.invoiceConfirmation,
          },
          bankAccountName: {
            validation: Yup.string().when('paymentMethod', {
              is: 'bankTransfer',
              then: Yup.string()
                .trim()
                .required('Please enter an account name.'),
              otherwise: Yup.string(),
            }),
            initialValue: debitDetailParentValues.bankAccountName,
          },
          bsbNumber: {
            validation: Yup.string().when('paymentMethod', {
              is: 'bankTransfer',
              then: Yup.string()
                .trim()
                .required('Please enter a valid BSB Number.')
                .min(7, 'Please enter a valid BSB Number.')
                .test(
                  'bsbNumber',
                  'Please enter a valid BSB Number.',
                  (bsbNumber: string) => {
                    if (
                      bankBranch.bsbNumber === bsbNumber &&
                      !bankBranch.valid
                    ) {
                      return false;
                    }

                    return true;
                  }
                ),
              otherwise: Yup.string(),
            }),
            initialValue: debitDetailParentValues.bsbNumber,
          },
          bankAccountNumber: {
            validation: Yup.string().when('paymentMethod', {
              is: 'bankTransfer',
              then: Yup.string()
                .required('Please enter your account number.')
                .min(6, 'Please enter a 6-9 digit account number.')
                .max(9, 'Please enter a 6-9 digit account number.'),
              otherwise: Yup.string(),
            }),
            initialValue: debitDetailParentValues.bankAccountNumber,
          },
        },
      }}
    >
      {({ validateSubForm, subFormFormikProps }) => {
        const { debitDetails: debitDetailValues } = subFormFormikProps.values;
        const { debitDetails: debitDetailErrors } = subFormFormikProps.errors;

        return (
          <FormSection
            id="DebitDetails"
            heading="Step 3: Your debit details"
            stepNumber="3"
            totalSteps="4"
            previousButton={
              <ButtonV2
                id="debitDetailsPreviousButton"
                secondary={true}
                type="button"
                onClick={() => {
                  resetFormContextError(
                    formContextValues,
                    formContextDispatch,
                    2
                  );
                  onPreviousClick();
                }}
              >
                Back
              </ButtonV2>
            }
            nextButton={
              <ButtonV2
                id="debitDetailsNextButton"
                disabled={
                  !subFormFormikProps.dirty ||
                  debitDetailErrors !== undefined ||
                  (debitDetailValues.paymentMethod === 'bankTransfer' &&
                    !bankBranch.valid)
                }
                loading={submitLoading}
                primary={true}
                type="button"
                onClick={async () => {
                  setSubmitLoading(true);
                  const { isValid } = await validateSubForm();

                  if (!isValid) {
                    setSubmitLoading(false);
                    return false;
                  }

                  formikProps.setValues({
                    ...formikProps.values,
                    ...subFormFormikProps.values,
                  });

                  if (debitDetailValues.paymentMethod === 'creditCard') {
                    try {
                      const response = await redirectToCreditCardPaymentPortal(
                        identifyYourAccountParentValues.accountNumber,
                        parentFormElectricity[0]
                          ? parentFormElectricity[0].accountNumber
                          : undefined,
                        parentFormGas[0]
                          ? parentFormGas[0].accountNumber
                          : undefined
                      );

                      if (response.status === 200) {
                        window.location.href = response.data.sessionUrl;
                      } else {
                        const error = {};
                        throw error;
                      }
                    } catch (e) {
                      setSubmitLoading(false);
                      setFormContextError(
                        formContextValues,
                        formContextDispatch,
                        2,
                        true,
                        'Other'
                      );
                    }
                  } else {
                    setSubmitLoading(false);
                    setFormContextBranchDetails(
                      formContextValues,
                      formContextDispatch,
                      bankBranch.valid,
                      bankBranch.bsbNumber,
                      bankBranch.displayName,
                      bankBranch.hideDisplayName
                    );

                    onNextClick();
                  }
                }}
              >
                Next
              </ButtonV2>
            }
          >
            {formContextError.steps[2].status ? (
              <ErrorNotification
                errorType={formContextError.steps[2].type}
                stateShortName={formContextValues.stateShortName}
                onClick={() =>
                  setFormContextError(
                    formContextValues,
                    formContextDispatch,
                    2,
                    false,
                    undefined
                  )
                }
              />
            ) : (
              <>
                <Element name="debitDetailsStart" id="debitDetailsStart" />
                <Box margin={{ bottom: '24px' }}>
                  <Heading level={3} color="#000F20" weight={700}>
                    How does Direct Debit work?
                  </Heading>
                  <Box margin={{ top: 'xsmall', bottom: '24px' }}>
                    <StyledText
                      lineHeight="28px"
                      style={{
                        fontWeight: 400,
                        fontSize: '18px',
                      }}
                    >
                      Direct debit is an automatic payment from your nominated
                      cheque, savings, transaction or credit card account. We’ll
                      debit the owed amount each time your bill’s due. You’ll
                      still receive a bill along with confirmation of your
                      payment.
                    </StyledText>
                  </Box>
                  <StyledText
                    lineHeight="28px"
                    style={{
                      fontWeight: 400,
                      fontSize: '18px',
                      marginBottom: '24px',
                    }}
                  >
                    To authorise direct debit, you agree:
                  </StyledText>
                  <Box margin={{ bottom: '24px' }}>
                    <Field
                      component={FormCheckBox}
                      name="debitDetails.accountConfirmation"
                      id="debitDetails.accountConfirmation"
                      data-testid="accountConfirmation"
                      label={
                        <StyledText
                          lineHeight="28px"
                          style={{
                            fontWeight: 400,
                            fontSize: '14px',
                          }}
                        >
                          I authorise Alinta Energy and its payment service
                          provider to direct debit my nominated bank account,
                          credit or debit card for any outstanding
                          amount/balance by the due date, until further notice,
                          as per the{' '}
                          <StyledLink
                            href="https://alintaenergy.com.au/directdebit-tcs"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            residential
                          </StyledLink>
                          /
                          <StyledLink
                            href="https://www.alintaenergy.com.au/-/jssmedia/alinta-website/documents/sme/22-024-direct-debit-form-sme-online.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            business
                          </StyledLink>{' '}
                          Direct Debit Service Agreement; and
                        </StyledText>
                      }
                    />
                  </Box>
                  <Box margin={{ bottom: '24px' }}>
                    <Field
                      component={FormCheckBox}
                      name="debitDetails.collectDataConfirmation"
                      id="debitDetails.collectDataConfirmation"
                      data-testid="collectDataConfirmation"
                      label={
                        <StyledText
                          lineHeight="28px"
                          style={{
                            fontWeight: 400,
                            fontSize: '18px',
                          }}
                        >
                          I am the account holder for the nominated payment
                          method, or am authorised by the account holder to set
                          up direct debit.
                        </StyledText>
                      }
                    />
                  </Box>
                  <Box>
                    <Field
                      component={FormCheckBox}
                      name="debitDetails.invoiceConfirmation"
                      id="debitDetails.invoiceConfirmation"
                      data-testid="invoiceConfirmation"
                      label={
                        <StyledText
                          lineHeight="28px"
                          style={{
                            fontWeight: 400,
                            fontSize: '14px',
                          }}
                        >
                          I am the account holder for the nominated payment
                          method, or am authorised by the account holder to set
                          up direct debit.
                        </StyledText>
                      }
                    />
                  </Box>
                </Box>
                <Box>
                  <StyledText margin={{ bottom: '10px' }}>
                    Select bank account or credit card
                  </StyledText>
                  <Field
                    id="paymentMethod"
                    name="debitDetails.paymentMethod"
                    ariaRequired={true}
                    component={FormRadioGroup}
                  >
                    <Box direction="row" gap="medium">
                      <RadioButton
                        id={'paymentMethod-bankTransfer'}
                        data-testid="bankTransferRadioButton"
                        label="Bank transfer"
                        value="bankTransfer"
                        onClick={() => {
                          if (debitDetailErrors) {
                            setDisableNext(true);
                          }
                        }}
                      />
                      <RadioButton
                        id={'paymentMethod-creditCard'}
                        data-testid="creditCardRadioButton"
                        label="Credit card"
                        value="creditCard"
                        onClick={() => {
                          if (
                            parentFormElectricity.length > 1 ||
                            parentFormGas.length > 1
                          ) {
                            setFormContextError(
                              formContextValues,
                              formContextDispatch,
                              1,
                              true,
                              'Credit card limit'
                            );
                            onPreviousClick();
                          } else {
                            setDisableNext(false);
                          }
                        }}
                      />
                    </Box>
                  </Field>
                </Box>
                {debitDetailValues.paymentMethod === 'bankTransfer' && (
                  <Box>
                    <FlexGrid direction="row">
                      <FlexGridItem md={4}>
                        <Field
                          component={FormTextInput}
                          name="debitDetails.bankAccountName"
                          id="debitDetails.bankAccountName"
                          label="Account name"
                        />
                      </FlexGridItem>
                      <FlexGridItem md={4}>
                        <Field
                          width="100%"
                          component={FormMaskedTextInput}
                          name="debitDetails.bsbNumber"
                          id="debitDetails.bsbNumber"
                          label="BSB number"
                          options={{
                            blocks: [3, 3],
                            delimiter: '-',
                            numericOnly: true,
                          }}
                          disabled={
                            debitDetailValues.bankAccountName.length === 0 ||
                            loadingBsbField
                          }
                          inputIcon={
                            loadingBsbField ? (
                              <LoaderIcon id="bsbLoader" />
                            ) : (
                              undefined
                            )
                          }
                          message={
                            !bankBranch.hideDisplayName
                              ? bankBranch.displayName
                              : undefined
                          }
                          onChange={() => {
                            if (!typingBsbField) {
                              setTypingBsbField(true);
                            }

                            if (!bankBranch.hideDisplayName) {
                              setBankBranch({
                                ...bankBranch,
                                hideDisplayName: true,
                              });
                            }

                            if (!disableNext) {
                              setDisableNext(true);
                            }
                          }}
                          onBlur={async () => {
                            setTypingBsbField(false);
                            const bsbNumber = debitDetailValues.bsbNumber;

                            if (
                              debitDetailErrors &&
                              debitDetailErrors.bsbNumber !== undefined
                            ) {
                              return false;
                            }

                            if (bankBranch.bsbNumber !== bsbNumber) {
                              setDisableNext(true);
                              setLoadingBsbField(true);
                              try {
                                const result = await validateBsbNumber(
                                  bsbNumber
                                );

                                if (result.status === 200) {
                                  setBankBranch({
                                    valid: true,
                                    bsbNumber: result.data.bsb,
                                    displayName: `${result.data.financialInstitutionCode}, ${result.data.suburb}`,
                                    hideDisplayName: false,
                                  });
                                  setLoadingBsbField(false);
                                  setDisableNext(false);
                                }
                              } catch (error) {
                                subFormFormikProps.setFieldError(
                                  'debitDetails.bsbNumber',
                                  'Please enter a valid BSB Number'
                                );
                                subFormFormikProps.setFieldTouched(
                                  'debitDetails.bsbNumber',
                                  true,
                                  false
                                );

                                setBankBranch({
                                  valid: false,
                                  bsbNumber: bsbNumber,
                                  displayName: '',
                                  hideDisplayName: true,
                                });
                                setLoadingBsbField(false);
                              }

                              return false;
                            } else if (
                              bankBranch.bsbNumber === bsbNumber &&
                              bankBranch.valid
                            ) {
                              setDisableNext(false);
                              setBankBranch({
                                ...bankBranch,
                                hideDisplayName: false,
                              });

                              return false;
                            }
                          }}
                        />
                      </FlexGridItem>
                      <FlexGridItem md={4}>
                        <Field
                          width="100%"
                          component={FormMaskedTextInput}
                          name="debitDetails.bankAccountNumber"
                          id="debitDetails.bankAccountNumber"
                          label="Account number"
                          type="tel"
                          options={{
                            numeral: true,
                            stripLeadingZeroes: false,
                            numeralThousandsGroupStyle: 'none',
                            numeralDecimalMark: 'none',
                            numeralPositiveOnly: true,
                          }}
                          disabled={
                            !bankBranch.valid ||
                            typingBsbField ||
                            loadingBsbField ||
                            (debitDetailErrors &&
                              debitDetailErrors.bsbNumber) ||
                            (debitDetailErrors &&
                              debitDetailErrors.bankAccountName)
                          }
                        />
                      </FlexGridItem>
                    </FlexGrid>
                  </Box>
                )}
              </>
            )}
          </FormSection>
        );
      }}
    </SubForm>
  );
};
